// Task lists read their markdown through marked's built-in list parser.
//
// The upstream TaskList tokenizer reads `- [ ] ` lines one at a time and cannot
// handle a line break inside an item (Shift-Enter = hardBreak, written as two
// trailing spaces and a newline). The item broke on save and reload: a
// continuation line without indent became a separate paragraph, one with indent
// a second paragraph in the item. Bullet lists go through marked's list parser
// (GFM lazy continuation lines) and round-trip correctly.
//
// So only `start` stays TipTap's; marked's list parser does the reading, and
// its `list_item` tokens (GFM `task` / `checked`) are mapped to the `taskItem`
// tokens TaskItem.parseMarkdown reads. Nested lists whose items are all tasks
// are mapped the same way.
//
// Delegating: the helper `@tiptap/markdown` gives tokenizers has only
// `blockTokens(src)` (block lexing with the same lexer), which would call this
// tokenizer again first. While delegating, this tokenizer steps aside
// (undefined), marked's built-in list reads, and only the first `list` token is
// used. Only its raw length is consumed; marked reads the rest again.
//
// `blockTokens` gets the source only up to where the list surely ends
// (`listBound`), not the whole rest: that made every task list re-lex the rest
// of the document (8 seconds per parse on a 150,000-character page with 72 task
// lists).
//
// Only the run of task items from the start of the list becomes a taskList. At
// the first item that is not a task the run ends (only what came before is
// consumed) and marked reads the rest as a normal list - the same cut as TipTap's.

import type { MarkdownLexerConfiguration, MarkdownToken } from "@tiptap/core";
import { TaskList } from "@tiptap/extension-task-list";
import { inlineLater } from "./active-lexer";
import { firstLine } from "./lines";

/** The parts of marked's list_item token used here. */
interface ListItemTokenLike {
  type: string;
  raw: string;
  task?: boolean;
  checked?: boolean;
  text?: string;
  tokens?: BlockTokenLike[];
}

/** The parts of marked's block tokens used here. */
interface BlockTokenLike {
  type: string;
  raw: string;
  text?: string;
  tokens?: unknown[];
  items?: ListItemTokenLike[];
}

/** The token TaskItem.parseMarkdown reads (a type alias so it fits `MarkdownToken`). */
type TaskItemToken = {
  type: "taskItem";
  raw: string;
  checked: boolean;
  text: string;
  tokens: unknown[];
  nestedTokens: unknown[];
};

type TaskListToken = {
  type: "taskList";
  raw: string;
  items: TaskItemToken[];
};

/** The tokenizer helpers used here. */
interface TokenizerHelpersLike {
  blockTokens(src: string): BlockTokenLike[];
  inlineTokens(src: string): unknown[];
}

const TASK_LIST_START_RE = /^\s*[-+*]\s+\[([ xX])\]\s+/;
/**
 * An empty item (`- [ ] ` alone: saved right after Enter). marked does not see
 * a task without text after `[ ]`, so it is caught here.
 */
const EMPTY_TASK_RE = /^\s*[-+*]\s+\[([ xX])\]\s*$/;
/** The `[ ] ` marked puts back on the paragraph text of a loose item. */
const CHECKBOX_PREFIX_RE = /^\[[ xX]\] /;

/** Re-entry guard while delegating (lexing is synchronous). */
let delegating = false;

/** A list item marker (`- ` / `* ` / `+ ` / `1. ` / `1) `); group 1 or 2 is its kind. */
const LIST_MARKER_RE = /^ {0,3}(?:([-+*])|\d{1,9}([.)]))(?:[ \t]|$)/;
/** An unindented heading or fence: ends a list whatever came before. */
const LIST_BREAK_RE = /^(?:#{1,6}(?:[ \t]|$)|```|~~~)/;

/**
 * Where the list starting at `src` ends at the latest (start of that line). An
 * unindented line ends the list when it is
 *   - after a blank line and not a list marker (neither continuation nor lazy line),
 *   - a marker of another kind than the first item (`*` after `-`: a new list),
 *   - a heading or a fence.
 * The list may end earlier; marked decides that. This only narrows the input,
 * it does not change where the list is cut.
 */
export function listBound(src: string): number {
  const kind = markerKind(firstLine(src));
  let prevBlank = false;
  let lineStart = src.indexOf("\n") + 1;
  while (lineStart > 0 && lineStart < src.length) {
    const nl = src.indexOf("\n", lineStart);
    const line = src.slice(lineStart, nl === -1 ? src.length : nl);
    const blank = line.trim() === "";
    if (!blank && !/^[ \t]/.test(line)) {
      const marker = markerKind(line);
      if (
        marker === undefined
          ? prevBlank || LIST_BREAK_RE.test(line)
          : marker !== kind
      ) {
        return lineStart;
      }
    }
    prevBlank = blank;
    lineStart = nl + 1;
  }
  return src.length;
}

function markerKind(line: string): string | undefined {
  const match = LIST_MARKER_RE.exec(line);
  return match ? (match[1] ?? match[2]) : undefined;
}

/** The run of task items from the start as a taskList token, or undefined. */
function taskRun(
  items: ListItemTokenLike[],
  helpers: TokenizerHelpersLike,
): TaskListToken | undefined {
  const run: TaskItemToken[] = [];
  for (const item of items) {
    const converted = toTaskItem(item, helpers);
    if (converted === undefined) break;
    run.push(converted);
  }
  if (run.length === 0) return undefined;
  return {
    type: "taskList",
    raw: run.map((item) => item.raw).join(""),
    items: run,
  };
}

/** A task item as a taskItem token; undefined when it is not a task. */
function toTaskItem(
  item: ListItemTokenLike,
  helpers: TokenizerHelpersLike,
): TaskItemToken | undefined {
  const empty = item.task ? null : EMPTY_TASK_RE.exec(firstLine(item.raw));
  if (!item.task && !empty) return undefined;
  const checked = empty
    ? empty[1].toLowerCase() === "x"
    : item.checked === true;

  // marked 17 puts a `checkbox` token first (at the block level when tight, at
  // the start of the first paragraph's inline tokens when loose, where the text
  // also gets `[ ] ` back). TipTap does not know it, so it is removed with the marker.
  const blocks = (item.tokens ?? []).filter((t) => t.type !== "checkbox");
  const [first, ...rest] = blocks;
  // A first paragraph (text when tight, paragraph when loose) is the item's
  // text. In an empty item that paragraph is the marker itself and is dropped.
  const firstIsText =
    first !== undefined &&
    (first.type === "text" || first.type === "paragraph");
  const text =
    firstIsText && !empty
      ? (first.text ?? "").replace(CHECKBOX_PREFIX_RE, "")
      : "";
  const nested = firstIsText ? rest : blocks;
  return {
    type: "taskItem",
    raw: item.raw,
    checked,
    text,
    // marked fills a text token's inline tokens at the end of lexing, so they
    // are empty now; the text without the marker is lexed again, at the end
    // like marked does, so later reference definitions count (active-lexer.ts).
    tokens:
      text === "" ? [] : (inlineLater(text) ?? helpers.inlineTokens(text)),
    nestedTokens: nested.map((token) => liftNestedTaskList(token, helpers)),
  };
}

/** A nested list whose items are all tasks becomes a taskList token. */
function liftNestedTaskList(
  token: BlockTokenLike,
  helpers: TokenizerHelpersLike,
): unknown {
  if (token.type !== "list" || !token.items) return token;
  const items: TaskItemToken[] = [];
  for (const item of token.items) {
    const converted = toTaskItem(item, helpers);
    if (converted === undefined) return token;
    items.push(converted);
  }
  return { type: "taskList", raw: token.raw, items };
}

export const TrameriaTaskList = TaskList.extend({
  markdownTokenizer: {
    name: "taskList",
    level: "block",
    start(src: string) {
      const index = src.match(TASK_LIST_START_RE)?.index;
      return index !== undefined ? index : -1;
    },
    tokenize(
      src: string,
      _tokens: MarkdownToken[],
      lexer: MarkdownLexerConfiguration,
    ): MarkdownToken | undefined {
      const helpers = lexer as unknown as TokenizerHelpersLike;
      if (delegating) return undefined;
      // marked calls every `tokenize` at every block boundary whatever `start`
      // said. Delegating unconditionally would cost the cube of the block
      // count even without any task item, so only when the source starts like one.
      if (
        !TASK_LIST_START_RE.test(src) &&
        !EMPTY_TASK_RE.test(firstLine(src))
      ) {
        return undefined;
      }
      delegating = true;
      let first: BlockTokenLike | undefined;
      try {
        first = helpers.blockTokens(src.slice(0, listBound(src)))[0];
      } finally {
        delegating = false;
      }
      if (first?.type !== "list" || !first.items) return undefined;
      // `MarkdownToken` has an index signature the precise type does not fit
      // (TipTap's own tokenizers return any).
      return taskRun(first.items, helpers) as unknown as
        | MarkdownToken
        | undefined;
    },
  },
});
